import React, { FC, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import styled from 'styled-components';

import WarningIconSm from 'components/Icons/WarningIconSm';
import SubmitButton from 'components/SubmitButton';
import { useValidatePin } from 'modules/corporateOffer/hooks/useValidatePin';
import theme from 'theme/theme';

import CodeInput from '../inputs/CodeInput';
import { ErrorText } from '../inputs/StyledFormInputs';

import { FIELDS } from './constants';
import { LinkContainer, PasscodeRecoveryLink } from './SignInStep';
import { OnboardingSubtitle, OnboardingTitle } from './StyledComponents';

type Props = {
  email: string;
  onSuccess: (pin: string) => void;
  onNotReceivedPin: () => void;
};

const VerifyEmailStep: FC<Props> = ({ email, onSuccess, onNotReceivedPin }) => {
  const [error, setError] = useState('');

  const [isCodeFilled, setIsCodeFilled] = useState(false);

  const {
    setFocus,
    handleSubmit,
    control,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm();

  const {
    mutate: validatePin,
    isLoading,
    isError,
  } = useValidatePin({
    onSuccess: () => {
      onSuccess(getValues(FIELDS).join(''));
    },
    onError: setError,
  });

  const onSubmit = () => {
    validatePin({
      email,
      pincode: getValues(FIELDS).join(''),
    });
  };

  const handleDidNotReceivePin = () => {
    onNotReceivedPin();
  };

  return (
    <Container>
      <header>
        <OnboardingTitle title="Verify your email address" error={isError} />
        <OnboardingSubtitle
          subtitle={`We sent a code to ${email}`}
          error={isError}
        />
        <Redline />
      </header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <CodeInputWrapper>
          <CodeInput
            label="Email verification code"
            error={''}
            fields={FIELDS}
            control={control as unknown as Control<FieldValues>}
            setFocus={setFocus}
            setIsCodeFilled={setIsCodeFilled}
            clearError={() => {
              setError('');
              clearErrors();
            }}
          />
        </CodeInputWrapper>
        {!!error && (
          <ErrorText $error={!!error}>
            <WarningIconSm color={theme.colors.primary} />
            {error}
          </ErrorText>
        )}
        <ButtonWrapper data-testid="submit-button-wrapper">
          <SubmitButton
            text="CONTINUE"
            isDisabled={
              Object.keys(errors).length > 0 || isLoading || !isCodeFilled
            }
            borderSize="xl"
            buttonSize="xl"
            fontSize="md"
          />
        </ButtonWrapper>
      </Form>
      <LinkContainer>
        <PasscodeRecoveryLink onClick={handleDidNotReceivePin}>
          Didn't receive your code?
        </PasscodeRecoveryLink>
      </LinkContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 40px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100%;
  }
`;

export const Redline = styled.div`
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.lightPink};
  height: 4px;
  width: 80px;
  margin-top: 33px;
  margin-left: auto;
  margin-right: auto;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }
`;

const Form = styled.form`
  margin: 70px auto 0;
  max-width: 600px;
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 335px;
    margin: 33px auto 0;
    padding-left: 0;
    padding-right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
`;

const CodeInputWrapper = styled.div`
  max-width: 300px;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 335px;
  }
`;

const ButtonWrapper = styled.div`
  margin: 38px auto 0;
  display: flex;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: 0;
  }
`;

export default VerifyEmailStep;
